import {inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {BicToastService} from '@buildinginformationcloud/bic-ui-components';
import {catchError, map, of} from 'rxjs';

import {StateConfigService} from '../state/state-config.service';
import {WorkflowService} from '../workflow.service';

/**
 * Einstieg in die beiden All-In Checks HOAI und LOIplus.
 *
 * <p>Beide laufen seit der myCheck-Umstellung über die Workflow-Engine und öffnen auf
 * `.../all-in-check/execute` je einen **im Adminbereich global bereitgestellten Prüflauf**.
 * Adressiert werden sie über die Plattformkonfiguration
 * ({@code bic.all-in.hoai-definition-id} / {@code bic.all-in.loiplus-definition-id}) – global
 * bereitgestellte Definitionen haben keinen Slug, und ihre IDs unterscheiden sich je Umgebung.
 *
 * <p>Sie sind projektlos und lassen sich aus jedem Projekt heraus direkt ausführen (Backend:
 * {@code WorkflowInstanceService.guardTriggerAllowed}); es braucht also keine projekteigene Kopie.
 * Regel- und Dateiauswahl kommen aus dem Manifest des jeweiligen Prüflaufs – welche Regelwerke
 * dort erlaubt sind, entscheidet der Adminbereich, nicht dieser Code.
 *
 * <p>Hier gebündelt, weil dieselben Einstiege an drei Stellen hängen: die Kachelseite, das
 * Call-to-Action-Banner und das Dashboard.
 *
 * <p>Der All-Right Check ({@code /quality}) läuft weiterhin über den alten Pfad und ist hier
 * bewusst nicht enthalten.
 */
@Injectable({providedIn: 'root'})
export class AllInCheckNavigationService {
  private router = inject(Router);
  private stateConfigService = inject(StateConfigService);
  private workflowService = inject(WorkflowService);
  private toastService = inject(BicToastService);

  /** True, solange eine Kachel ihren Prüflauf auflöst (ein kurzer Roundtrip, s. unten). */
  resolving = false;

  /** HOAI-Prüfung starten. */
  startHoaiCheck(): void {
    this.startConfiguredCheck(this.stateConfigService.hoaiDefinitionId, 'HOAI-Prüfung', 'hoai');
  }

  /** LOIplus-Prüfung starten. */
  startLoiplusCheck(): void {
    this.startConfiguredCheck(this.stateConfigService.loiplusDefinitionId, 'LOIplus-Prüfung', 'loiplus');
  }

  /**
   * Öffnet die Ausführung des konfigurierten Prüflaufs.
   *
   * <p>Konfiguriert werden darf **die Freigabe-ID oder die Definitions-ID**. Beides zuzulassen ist
   * kein Komfort, sondern folgt der Oberfläche: im Adminbereich lautet die Route
   * `admin/check-runs/:shareId/builder`, dort hat man also die *Freigabe*-ID vor sich, während der
   * Ausführungspfad die *Definitions*-ID braucht. Deshalb wird die Liste der bereitgestellten
   * Prüfläufe abgefragt und ein Treffer auf `shareId` in seine `definitionId` übersetzt; passt
   * nichts, gilt der Wert unverändert als Definitions-ID.
   *
   * <p>Fehlt die Konfiguration, wird das mit dem Namen der Property gemeldet – kein Rückfall auf
   * irgendeinen anderen Prüflauf, denn welcher „der richtige" ist, weiß nur die Konfiguration.
   */
  private startConfiguredCheck(configuredId: string | undefined, label: string, propertyKey: string): void {
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!projectId || this.resolving) return;

    if (!configuredId) {
      this.toastService.error(`Die ${label} ist auf dieser Umgebung nicht konfiguriert `
        + `(bic.all-in.${propertyKey}-definition-id im Backend).`);
      return;
    }

    this.resolving = true;
    this.workflowService.listSharedForMe(projectId).pipe(
      map(shares => shares.find(share => share.shareId === configuredId)),
      // Ohne die Freigabeliste bleibt der konfigurierte Wert die beste Annahme – der Fall „ID ist
      // schon eine Definitions-ID" braucht die Liste gar nicht.
      catchError(() => of(undefined)),
    ).subscribe(share => {
      this.resolving = false;
      if (share && share.status === 'DRAFT') {
        this.toastService.error(`Der bereitgestellte Prüflauf "${share.name}" ist unvollständig `
          + `gespeichert (Status DRAFT) und kann nicht ausgeführt werden.`);
        return;
      }
      this.router.navigate(['projects', projectId, 'all-in-check', 'execute'], {
        queryParams: {definitionId: share?.definitionId ?? configuredId},
      });
    });
  }
}
